import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import type { LtnToastService } from '../ltn-toast/ltn-toast-service.js';
import { ltnToastServiceContext } from '../ns-services/contexts.js';

import { prefersReducedMotion } from '../helpers/motion.js';
import './ns-tutorial-overlay.js';
import { eventWithin, findTarget, waitForTargets } from './resolve-target.js';
import {
  OPTIONAL_TIMEOUT_MS,
  PAGE_TOURS,
  STORAGE_KEY,
  TARGET_TIMEOUT_MS,
  completeStep,
  currentStep,
  endTour,
  fillText,
  finishTour,
  isTourDone,
  parseStoredState,
  reopenSteps,
  setShown,
  shouldRun,
  signalCompletes,
  stepPosition,
  stepTourIds,
  type StoredTutorialState,
  type TourContext,
  type TourId,
  type TourStep,
  type TutorialSignal,
} from './tour-state.js';
import tours from './tours/index.js';
import { JOB_STEPS } from './tours/welcome.js';
import { WORKFLOW_STEPS } from './tours/workflows.js';

// What the welcome tour needs from ns-feed (data-tour-id="feed")
export interface TourFeed extends HTMLElement {
  readonly tourFeedState: 'loading' | 'empty' | 'has-job';
  openFirstJob(): Promise<'opened' | 'already-open' | 'failed'>;
  loadNewItems(): boolean;
}

// What the Workflows tour needs from the workflow list (data-tour-id="workflow-feed")
export interface TourWorkflowFeed extends HTMLElement {
  readonly tourWorkflowState: 'loading' | 'empty' | 'has-workflow';
  openFirstWorkflow(): Promise<'opened' | 'already-open' | 'failed'>;
}

interface Display {
  step: TourStep;
  targets: HTMLElement[];
  interactive: HTMLElement[];
  anchor: HTMLElement;
  hideOn: HTMLElement[];
  text: string;
  index: number;
  total: number;
}

// How long the welcome tour waits for the feed's first data before deciding whether there's a job
const FEED_TIMEOUT_MS = 20000;
const FEED_POLL_MS = 250;
const LOADER_POLL_MS = 150;
// How long Next waits for a job behind the "new items" pill to load
const NEW_JOB_TIMEOUT_MS = 3000;

// Automated browsers (the e2e specs, the demo recordings) only get the guide when they set the key
// themselves, so its locked page doesn't swallow their clicks
function readState(): StoredTutorialState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null && navigator.webdriver) return endTour(parseStoredState(null));
    return parseStoredState(raw);
  } catch {
    return parseStoredState(null);
  }
}

// The in-app guide: runs the onboarding walkthrough and the welcome tour, keeps their progress in
// this browser and draws them through ns-tutorial-overlay. NSApp creates it, tells it the route and
// renders it in its shadow root, so the overlay inherits the theme. The page tells it what happened
// with bubbling, composed `ns-tutorial-signal` events.
@customElement('ns-tutorial-service')
export class NSTutorialService extends LitElement {
  @state() private __display?: Display;

  @state() private __bubbleHidden = false;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  private __state = readState();

  private __active?: TourId;

  private __route?: string;

  private __stage?: string;

  private __context: TourContext = {};

  private __abort?: AbortController;

  // The step being resolved or shown, so a repeated signal or route change doesn't restart it
  private __stepId?: string;

  // Step groups (a tab, an opened workflow) whose optional sections have been checked, as `tour:group`
  private __checkedGroups = new Set<string>();

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('ns-tutorial-signal', this.__onSignal);
    window.addEventListener('onboarding-complete', this.__onOnboardingComplete);
    window.addEventListener('click', this.__onClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('ns-tutorial-signal', this.__onSignal);
    window.removeEventListener('onboarding-complete', this.__onOnboardingComplete);
    window.removeEventListener('click', this.__onClick, true);
    this.__abort?.abort();
  }

  // My Account's "Show walkthrough" switch
  // On while any tour that shows the app around would still run, so a user who skipped or finished
  // them can turn them back on
  get shown(): boolean {
    return PAGE_TOURS.some((id) => shouldRun(this.__state, id));
  }

  setShown(shown: boolean) {
    this.__save(setShown(this.__state, shown));
    if (!shown) this.__clear();
  }

  // NSApp calls this with the route's first segment on every route change
  routeChanged(root: string) {
    if (root === this.__route) return;
    this.__route = root;
    if (root === 'onboarding') this.__activate('onboarding');
    else if (root === 'feed') void this.__startWelcome();
    else if (root === 'workflows') void this.__startWorkflows();
    else if (root === 'my-account') this.__activate(this.__pageTourRuns('account') ? 'account' : undefined);
    else this.__activate(undefined);
  }

  private __save(next: StoredTutorialState) {
    this.__state = next;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage blocked: progress lasts until the page reloads
    }
  }

  private __activate(tourId: TourId | undefined) {
    if (tourId !== this.__active) this.__clear();
    this.__active = tourId;
    this.__refresh();
  }

  // The welcome tour needs to know whether the feed has a job to open, so it starts once the feed
  // has its first data
  private async __startWelcome() {
    if (!shouldRun(this.__state, 'welcome')) return this.__activate(undefined);
    if (this.__active === 'welcome') return;
    this.__activate(undefined);
    const started = performance.now();
    while (performance.now() - started < FEED_TIMEOUT_MS) {
      if (this.__route !== 'feed') return;
      const feed = this.__feed;
      if (feed && feed.tourFeedState !== 'loading') break;
      await new Promise((resolve) => setTimeout(resolve, FEED_POLL_MS));
    }
    // The route moved on, or another start got there first, while the feed loaded
    if (this.__route !== 'feed' || this.__isActive('welcome')) return;
    this.__activate('welcome');
  }

  // A page's own tour runs on the first visit to the page, once the welcome tour is finished or skipped
  private __pageTourRuns(tourId: TourId): boolean {
    return isTourDone(this.__state, 'welcome') && shouldRun(this.__state, tourId);
  }

  // Like the welcome tour, the Workflows tour starts once the list has its first data, which decides
  // whether there's a workflow to open
  private async __startWorkflows() {
    if (!this.__pageTourRuns('workflows')) return this.__activate(undefined);
    if (this.__active === 'workflows') return;
    this.__activate(undefined);
    const started = performance.now();
    while (performance.now() - started < FEED_TIMEOUT_MS) {
      if (this.__route !== 'workflows') return;
      const list = this.__workflowFeed;
      if (list && list.tourWorkflowState !== 'loading') break;
      await new Promise((resolve) => setTimeout(resolve, FEED_POLL_MS));
    }
    if (this.__route !== 'workflows' || this.__isActive('workflows')) return;
    this.__activate('workflows');
  }

  private get __workflowFeed(): TourWorkflowFeed | null {
    return this.__root ? (findTarget(this.__root, 'workflow-feed') as TourWorkflowFeed | null) : null;
  }

  private __readWorkflowFeed() {
    const list = this.__workflowFeed;
    if (!list || list.tourWorkflowState === 'loading') return;
    this.__context = { ...this.__context, hasWorkflow: list.tourWorkflowState === 'has-workflow' };
  }

  private get __feed(): TourFeed | null {
    return this.__root ? (findTarget(this.__root, 'feed') as TourFeed | null) : null;
  }

  private async __feedHasJob() {
    const started = performance.now();
    while (this.__feed?.tourFeedState !== 'has-job' && performance.now() - started < NEW_JOB_TIMEOUT_MS) {
      await new Promise((resolve) => setTimeout(resolve, FEED_POLL_MS));
    }
  }

  private __readFeed() {
    const feed = this.__feed;
    if (!feed || feed.tourFeedState === 'loading') return;
    this.__context = { ...this.__context, hasJob: feed.tourFeedState === 'has-job' };
    if (feed.tourFeedState === 'empty') feed.loadNewItems();
  }

  private __isActive(tourId: TourId): boolean {
    return this.__active === tourId;
  }

  private get __root(): ParentNode | null {
    return this.getRootNode() as ParentNode | null;
  }

  private __clear() {
    this.__toast?.endAnswer();
    this.__abort?.abort();
    this.__abort = undefined;
    this.__stepId = undefined;
    this.__display = undefined;
    this.__bubbleHidden = false;
  }

  private __refresh() {
    if (!this.__active) return this.__clear();
    // A new user's first job can arrive while the tour runs, so whether there's one is read afresh
    if (this.__active === 'welcome') this.__readFeed();
    if (this.__active === 'workflows') this.__readWorkflowFeed();
    const tour = tours[this.__active];
    const step = currentStep(tour, this.__state, this.__context, this.__stage);
    if (!step) return this.__clear();
    if (step.id === this.__stepId) return;
    void this.__show(tour.id, step);
  }

  private async __show(tourId: TourId, step: TourStep) {
    this.__clear();
    this.__stepId = step.id;
    const abort = new AbortController();
    this.__abort = abort;
    const root = this.__root;
    // The loader covers the page while it loads; the bubble would show above it in the top layer
    if (root) await loaderGone(root, abort.signal);
    const timeout = step.optional ? OPTIONAL_TIMEOUT_MS : TARGET_TIMEOUT_MS;
    // A step about a tab opens the tab first (an optional step's tab may not be there)
    if (root && step.select && !abort.signal.aborted) await selectTab(root, step.select, abort.signal, timeout);
    // The first step on a view finds out which of the view's optional sections are there, so the count
    // only changes here rather than as the tour reaches each missing section
    if (root && step.group && !abort.signal.aborted) await this.__checkGroup(tourId, step, root, abort.signal);
    if (abort.signal.aborted) return;
    if (this.__context.absent?.includes(step.id)) {
      this.__stepId = undefined;
      return this.__refresh();
    }
    const found =
      root && !abort.signal.aborted ? await waitForTargets(root, stepTourIds(step), abort.signal, timeout) : null;
    if (abort.signal.aborted) return;
    const tour = tours[tourId];
    if (!found) {
      // A target that never appeared: skip the step rather than stall the tour. An optional step's
      // section isn't on the page for this user, so it's out of the count too.
      if (step.optional) this.__context = { ...this.__context, absent: [...(this.__context.absent ?? []), step.id] };
      this.__stepId = undefined;
      this.__save(completeStep(this.__state, tour, this.__context, step.id));
      return this.__refresh();
    }

    const byId = (id: string) => found[stepTourIds(step).indexOf(id)];
    const targets = step.targets.map(byId);
    const anchor = byId(step.anchor ?? step.targets[0]);
    if (!isInViewport(anchor)) {
      anchor.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    const { index, total } = stepPosition(tour, this.__context, step.id);
    this.__display = {
      step,
      targets,
      interactive: step.passive ? [] : targets,
      anchor,
      hideOn: (step.hideOn ?? []).map(byId),
      text: fillText(step.text, this.__context),
      index,
      total,
    };
  }

  // Leaves out the optional steps of the step's group whose section isn't on the page. A step on
  // another tab counts as there when its tab is, since that tab's content isn't on the page yet.
  private async __checkGroup(tourId: TourId, step: TourStep, root: ParentNode, signal: AbortSignal) {
    const key = `${tourId}:${step.group}`;
    if (this.__checkedGroups.has(key)) return;
    const tour = tours[tourId];
    const optional = tour.steps.filter(
      (other) => other.group === step.group && other.optional && !this.__context.absent?.includes(other.id),
    );
    const checks = await Promise.all(
      optional.map(async (other) => {
        if (other.select && other.select !== step.select) {
          return { other, found: await waitForTargets(root, [other.select], signal, OPTIONAL_TIMEOUT_MS) };
        }
        if (other.decidedBy) {
          // Once what it renders with is on the page, the section is either there too or not coming
          const decided = await waitForTargets(root, [other.decidedBy], signal, OPTIONAL_TIMEOUT_MS);
          return { other, found: decided && stepTourIds(other).every((id) => !!findTarget(root, id)) };
        }
        return { other, found: await waitForTargets(root, stepTourIds(other), signal, OPTIONAL_TIMEOUT_MS) };
      }),
    );
    if (signal.aborted) return;
    this.__checkedGroups.add(key);
    const missing = checks.filter(({ found }) => !found).map(({ other }) => other.id);
    if (!missing.length) return;
    this.__context = { ...this.__context, absent: [...(this.__context.absent ?? []), ...missing] };
    for (const id of missing) this.__save(completeStep(this.__state, tour, this.__context, id));
  }

  private __onOnboardingComplete = () => this.__handleSignal({ type: 'onboarding-complete' });

  private __onSignal = (event: Event) => {
    const signal = (event as CustomEvent<TutorialSignal>).detail;
    if (signal) this.__handleSignal(signal);
  };

  private __handleSignal(signal: TutorialSignal) {
    if (signal.type === 'stage') {
      this.__stage = signal.stage;
      this.__context = { ...this.__context, enrolledCompany: signal.enrolledCompany, companyName: signal.companyName };
    }

    const step = this.__display?.step ?? this.__pendingStep();
    if (this.__active && step && signalCompletes(step, signal)) {
      this.__save(completeStep(this.__state, tours[this.__active], this.__context, step.id));
      this.__stepId = undefined;
    }
    // Once onboarding is finished, its walkthrough has nothing left to guide
    if (signal.type === 'onboarding-complete') this.__save(finishTour(this.__state, 'onboarding'));
    if (signal.type === 'job-closed' && this.__active === 'welcome' && step && JOB_STEPS.includes(step.id)) {
      this.__save(reopenSteps(this.__state, 'welcome', JOB_STEPS));
      this.__stepId = undefined;
    }
    // The same for an opened workflow: closing it goes back to opening it
    if (
      signal.type === 'workflow-closed' &&
      this.__active === 'workflows' &&
      step &&
      WORKFLOW_STEPS.includes(step.id)
    ) {
      this.__save(reopenSteps(this.__state, 'workflows', WORKFLOW_STEPS));
      this.__stepId = undefined;
    }
    this.__refresh();
  }

  // The step still waiting for its targets, which a signal can complete before it shows
  private __pendingStep(): TourStep | undefined {
    if (!this.__active || !this.__stepId) return undefined;
    return tours[this.__active].steps.find((step) => step.id === this.__stepId);
  }

  // A click on the step's action hides the bubble until the action succeeds; if it fails, the
  // page's error shows and the bubble stays hidden
  private __onClick = (event: MouseEvent) => {
    const display = this.__display;
    if (!display?.hideOn.length || !eventWithin(event, display.hideOn)) return;
    this.__bubbleHidden = true;
    // A toast raised now answers the click (an error), so it stays usable
    this.__toast?.expectAnswer();
  };

  private async __next() {
    const display = this.__display;
    if (!display || !this.__active) return;
    if (display.step.onNext === 'open-first-job') {
      // The job-opened signal completes the step once the card is open
      this.__bubbleHidden = true;
      const opened = (await this.__feed?.openFirstJob()) ?? 'failed';
      if (this.__display?.step !== display.step) return;
      if (opened === 'failed') this.__bubbleHidden = false;
      if (opened === 'already-open') this.__handleSignal({ type: 'job-opened' });
      return;
    }
    if (display.step.onNext === 'open-first-workflow') {
      // The workflow-opened signal completes the step once the workflow is open
      this.__bubbleHidden = true;
      const opened = (await this.__workflowFeed?.openFirstWorkflow()) ?? 'failed';
      if (this.__display?.step !== display.step) return;
      if (opened === 'failed') this.__bubbleHidden = false;
      if (opened === 'already-open') this.__handleSignal({ type: 'workflow-opened' });
      return;
    }
    // A job may have arrived since the step showed, which decides whether this was the last step: one
    // behind the "new items" pill is loaded first, then the step relabelled rather than the tour ended
    if (this.__active === 'welcome') {
      if (this.__feed?.tourFeedState === 'empty' && this.__feed.loadNewItems()) await this.__feedHasJob();
      if (this.__display?.step !== display.step) return;
      this.__readFeed();
      const { index, total } = stepPosition(tours.welcome, this.__context, display.step.id);
      if (total !== display.total) {
        this.__display = { ...display, index, total };
        return;
      }
    }
    this.__save(completeStep(this.__state, tours[this.__active], this.__context, display.step.id));
    this.__stepId = undefined;
    this.__refresh();
  }

  private __skipSection() {
    if (!this.__active) return;
    this.__save(finishTour(this.__state, this.__active));
    this.__clear();
  }

  private __end() {
    this.__save(endTour(this.__state));
    this.__clear();
  }

  // Notices sit above the scrim, so the lock mutes them too, for as long as it's up
  protected updated() {
    if (this.__toast) this.__toast.muted = !!this.__display;
  }

  render() {
    const display = this.__display;
    return html`
      <ns-tutorial-overlay
        .targets=${display?.targets ?? []}
        .interactive=${display?.interactive ?? []}
        .anchor=${display?.anchor}
        .text=${display?.text ?? ''}
        .placement=${display?.step.placement ?? 'bottom'}
        .index=${display?.index ?? 0}
        .total=${display?.total ?? 0}
        ?show-next=${display?.step.advance === 'next'}
        ?last-step=${!!display && display.index === display.total}
        ?bubble-hidden=${this.__bubbleHidden}
        @tutorial-next=${() => void this.__next()}
        @tutorial-skip-section=${() => this.__skipSection()}
        @tutorial-end=${() => this.__end()}
      ></ns-tutorial-overlay>
    `;
  }
}

// Clicks a tab (an ns-pill-tabs button with a data-tour-id) unless it's already the open one
async function selectTab(root: ParentNode, tourId: string, signal: AbortSignal, timeoutMs: number) {
  const [tab] = (await waitForTargets(root, [tourId], signal, timeoutMs)) ?? [];
  if (tab && !signal.aborted && tab.getAttribute('aria-selected') !== 'true') tab.click();
}

async function loaderGone(root: ParentNode, signal: AbortSignal) {
  const loader = root.querySelector('ns-loader') as (HTMLElement & { isLoading?: boolean }) | null;
  const covering = () => !!loader && (loader.isLoading || loader.matches(':popover-open'));
  while (covering() && !signal.aborted) await new Promise((resolve) => setTimeout(resolve, LOADER_POLL_MS));
}

function isInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-tutorial-service': NSTutorialService;
  }
  interface WindowEventMap {
    'ns-tutorial-signal': CustomEvent<TutorialSignal>;
  }
}
